<template>
  <v-card class="pt-2 rounded-0">
    <v-toolbar class="elevation-5" color="secondary" dark height="28px">
      <v-toolbar-title class="ml-6 mt-1">Sessions
      </v-toolbar-title>
      <span :class="{'caption':$vuetify.breakpoint.xs}" style="margin: 5px 0 0 8px">
        {{ listLength > 0 ? listLength.toLocaleString('en', {useGrouping:true}) : ''}}</span>
      <information-dialog-icon confirm-class="mr-3 mb-1 title" dialog-width="550" icon-color="white"
                               icon-style="margin: 2px 0 -1px -4px" tclass="headline grey lighten-2 mb-8"
                               title="Sessions">
        <p>Fill fields and click LOG, DAILY, or PERIOD, to see sessions.</p>
        <p>If time period is more than one year, only DAILY and PERIOD will be enabled.</p>
        <p>Click <v-btn fab icolor="black" elevation="5" style="transform: scale(.8)" x-small><v-icon>mdi-update</v-icon></v-btn> to refresh search results</p>
        <p>Click <v-btn color="secondary" fab style="transform: scale(.8)" x-small><v-icon>mdi-plus</v-icon></v-btn> to insert a session</p>
        <p>Click <v-icon>mdi-pencil</v-icon> to edit a session (LOG only)</p>
      </information-dialog-icon>
      <v-spacer/>
      <v-text-field
        append-icon="mdi-magnify"
        class="ml-n3 mb-1 mr-1"
        clearable
        dense
        hide-details
        :label="$vuetify.breakpoint.name === 'xs' ? '' : 'Search'"
        single-line
        :style="$vuetify.breakpoint.name === 'xs' ? 'font-size: x-small' : ''"
        style="margin-top: 1px; max-width: 26vw"
        v-model="search"/>
    </v-toolbar>
    <v-form ref="form" v-model="valid">
      <v-data-table class="pt-2 pb-0 elevation-2"
                    dense
                    fixed-header
                    :headers="headers"
                    height="calc(100vh - var(--offset) - 46px)"
                    hide-default-footer
                    :items="list"
                    :items-per-page="-1"
                    :key="headers[0].width"
                    :loading="loading"
                    loading-text="Data loading... Please wait..."
                    multi-sort
                    :mobile-breakpoint='0'
                    :no-data-text="noDataText"
                    :search="search"
                    :style="$_cssVars">
        <template v-slot:item.actions="{ item }">
          <v-lazy>
            <button-tip btnclass="py-0 mx-n1" @click="editSession(item)" icon iname="mdi-pencil" small style="max-height: 24px !important" tip="Edit Session" v-if="!!item.id"/>
          </v-lazy>
        </template>
        <template v-slot:header>
          <th class="pb-3" :key="field.value + refresh" v-for="field in headers" :style="field.value === 'actions' ? {whiteSpace: 'nowrap', paddingLeft: '8px', paddingRight: '0', textAlign: 'left'} : {}">
            <span v-if="field.value === 'actions'" style="display: inline-flex; gap: 4px;">
              <button-tip btnclass="" @click="refreshSessions" elevation="5" fab icolor="black"
                          iname="mdi-update" tip="Refresh Sessions" xsmall/>
              <button-tip btnclass="" @click="createSession" color="secondary" elevation="5" fab icolor="white"
                          iname="mdi-plus" tip="Create Session" xsmall/>
            </span>
            <smart-input
              class="ml-4 pt-3 mb-n2"
              dense
              :disabled="field.value === 'username' && !$_can('hours-override')"
              :items="dropdownItems[field.list]"
              :key="refresh"
              :label="field.text"
              single-line
              :style="{ fontWeight: 'lighter', width: field.width, fontSize: '14px' }"
              :type="'VAutocomplete'"
              v-if="field.value === 'username' || field.value === 'type'"
              v-model="hoursSearch[field.value]"/>
            <div class="px-0 mb-4" :key="refreshButtons" v-else-if="field.value === 'time'">
              <v-row class="ml-1 nopad d-flex justify-center">
                <v-btn class="box" @click="getHours('Log')" color='secondary'
                       :disabled="!valid || periodMoreThanYear || !hoursSearch.username || hoursSearch.username === 'All'
                        || !hoursSearch.formatted_login || !hoursSearch.logout"
                       min-width="43" small>Log</v-btn>
                <v-btn class="box" @click="getHours('Daily')" color='secondary'
                       :disabled="!valid || !hoursSearch.username || hoursSearch.username === 'All'
                        || !hoursSearch.formatted_login || !hoursSearch.logout"
                       min-width="53" small>Daily</v-btn>
                <v-btn class="box" @click="getHours('Period')" color='secondary' :disabled="!valid
                        || !hoursSearch.formatted_login || !hoursSearch.logout"
                       min-width="63" small>Period</v-btn>
              </v-row>
              <v-row v-if="list.sum && !search">
                <div class="mt-3">sum</div>
                <v-col class="d-flex justify-end" style="margin-right: 34px">{{ list.sum }}</v-col>
              </v-row>
              <v-row class="mt-n6 mb-n3" v-if="list.core && !search">
                <div class="mt-3">core</div>
                <v-col class="d-flex justify-end" style="margin-right: 34px">{{ list.core }}</v-col>
              </v-row>
              <v-row class="my-n5" v-else/>
            </div>
            <div v-if="['formatted_login','logout'].includes(field.value)" class="mx-2 down">
              <!-- 20 Nov 2025 JCH temp: set menuNudgeTop=0 for xs (was 330) -->
              <date-picker-formatted
                clearable
                dense
                :date-formatted-prop="hoursSearch[field.value]"
                :format="dateFormat"
                :format-loosely-valid="dateFormatLooselyValid"
                :label="field.text"
                :max="today"
                :menuNudgeRight="$vuetify.breakpoint.name === 'xs' ? '0' : '120'"
                :menuNudgeTop="$vuetify.breakpoint.name === 'xs' ? '-4' : '-4'"
                menu-wrapper
                no-title
                offset-y
                picker-color="secondary"
                :rules="[rules.date, rules.startBeforeEnd, rules.required]"
                single-line
                :textFieldClass="'mt-2'"
                v-model="hoursSearch[field.value]"/>
            </div>
          </th>
        </template>
        <template v-slot:item.formatted_login="{ item }"><!-- 2025-08-04 NEW: Local formatted Start/End display -->
          <span>{{ item.login_iso ? formatLocalDate(item.login_iso) : (item.formatted_login || formatLocalDate(item.login)) }}</span>
        </template>
        <template v-slot:item.logout="{ item }">
          <span>{{ item.logout_iso ? formatLocalDate(item.logout_iso) : formatLocalDate(item.logout) }}</span>
        </template>
      </v-data-table>
    </v-form>

    <ErrorDialog ref="errorDialog"/>

  </v-card>

</template>

<script>
import moment from "moment";
import { UsersAPIService } from '@/servicehandlers/UsersAPIService';

const usersAPIService = new UsersAPIService();

export default {
  name: 'Sessions',
  props: ['items'],

  data() {
    return {
      dateFormat: 'DD MMM YYYY',
      dateFormatLooselyValid: 'D MMM YYYY',// for DatePickerFormatted.vue to accept and add leading zeros
      dropdownItems: {
        types: ['Any', 'Manual Logout', 'Automatic Logout', 'Vacation', 'Holiday', 'Floater', 'Sick Leave', 'Jury Duty',
          'Unpaid Leave', 'Penalty', 'Personal', 'Combined']
      },
      get: 'Time Entries',// default switch set to 'Time Entries'
      headerExtra1: [{value: 'extra1', text: '', width: "0", sortable: false },],// add space to larger screens...
      headerExtra2: [{value: 'extra2', text: '', width: "0", sortable: false },],// ...looks better than padding
      headersSmallScreen: [
        {value: 'actions',  text: '',width: "60px" }, // 14 Nov 2025 JCH minimum width to keep buttons horizontal
        {value: 'username',       text: 'Username', width: "100px", sortable: true, list: 'usernames' },
        {value: 'formatted_login',text: 'Start',    width: "210px", sortable: false,align: 'start' },
        {value: 'logout',         text: 'End',      width: "210px", sortable: false,align: 'start' },
        {value: 'type',           text: 'Type',     width: "155px", sortable: true, align: 'start', list: 'types' },
        {value: 'time',           text: 'Totals',   width: "155px", sortable: true, align: 'right', sort: this.sortHours },
      ],
      hoursSearch: {},
      list: [],
      listLength: '',
      loading: false,
      noDataText: 'Fill fields and click LOG, DAILY, or PERIOD',
      refresh: false,
      refreshButtons: false,
      rules: {
        date: (x) => (!x || moment(x, 'D MMM YYYY',true).isValid()
            || moment(x, 'DD MMM YYYY',true).isValid()) || 'Must be D MMM YYYY',
        required: (x) => !!x || 'Field is required',
        startBeforeEnd: (x) => (!x || this.startBeforeEnd) || 'End date before Start',
      },
      search: '',
      today: moment().format('YYYY-MM-DD'),
      totType: undefined,
      usernames: [],
      valid: false,
    }
  },

  watch: {
    get() {// if getting new data, clear search field
      this.search = '';
    },

    hoursSearch: {
      handler() {// if an hoursSearch field changes...
        this.list = [];// ...clear old results
        this.$nextTick(() => {
          this.$refs.form.validate();// ...validate form so error messages will update
        });
      },
      deep: true,
    },

    periodMoreThanYear() {// if dates periodMoreThanYear changes, refresh which buttons are disabled
      this.refreshButtons = !this.refreshButtons;
    },
  },

  computed: {
    headers() {
      // on larger screens + more space to table sides by adding extra headers (cols) - looks better than padding
      return ['xs','sm'].includes(this.$vuetify.breakpoint.name)
            ? this.headersSmallScreen : [...this.headerExtra1, ...this.headersSmallScreen, ...this.headerExtra2];
    },

    periodMoreThanYear() {// is the difference between start date and end date more than 1 year?
      if (!this.hoursSearch.formatted_login || !this.hoursSearch.logout) return false;
      let logout = this.hoursSearch.logout;
      let formatted_login = this.hoursSearch.formatted_login;
      let yearsBetweenDates = moment(logout).diff(moment(formatted_login), 'years', true);
      return (yearsBetweenDates > 1);
    },

    startBeforeEnd() {// is the start date the sameOrBefore the end date?
      if (!this.hoursSearch.formatted_login || !this.hoursSearch.logout) return true;
      return moment(this.hoursSearch.formatted_login).isSameOrBefore(moment(this.hoursSearch.logout));
    },
  },

  created() {
    if (this.$_can('hours-override')) usersAPIService.getActiveUsers(this.$router).then((users) => {// dropdown
      this.dropdownItems['usernames'] = [];
      for (let user of users) this.dropdownItems['usernames'].push(user.username);
      this.dropdownItems['usernames'].sort();
      this.dropdownItems['usernames'].unshift('All');// only for "Period" - lists usernames,hours for that period
      this.refresh = !this.refresh;
    }).catch((e) => this.$refs.errorDialog.showError({ forDevelopers: e }));

    
    const today = moment().format('DD MMM YYYY');// 17 Nov 2025 JCH initialize hoursSearch fields to default values
    this.hoursSearch = {
      username: this.$store.getters.username,
      formatted_login: today,
      logout: today,
      type: 'Any',
    };

    window.refreshSessionsList = () => {// 17 Nov 2025 JCH Expose global func for popups refresh sessions
      if (this.$options.name === 'Sessions') {
        this.refreshSessions();
      }
    };
  },

  mounted() {
    window.sessionsComponent = this;// Store component ref--popups access via window.opener
  },

  beforeDestroy() {// 14 Nov 2025 JCH Clean up window references (won't affect existing popup refs)
    if (window.refreshSessionsList) {
      delete window.refreshSessionsList;
    }
    if (window.sessionsComponent === this) {
      delete window.sessionsComponent;
    }
  },

  methods: {
    refreshSessions() {// 14 Nov 2025 JCH Refresh with same filters last used
      if (this.totType) {
        this.getHours(this.totType);
      }
    },

    refreshFromPopup() {// 14 Nov 2025 JCH Expose refresh method for popups to call
      this.refreshSessions();
    },

    createSession() {
      let baseUrl = window.location.origin;
      window.open(baseUrl +  '/sessionEdit', '_blank',
          'resizeable=yes, width=' + (500) + ', left=' + (500) + ', top=' + (22) + ', height=' + (700));
    },

    editSession(session) {
      if (!session || !session.id) return;
      let baseUrl = window.location.origin;
      window.open(baseUrl +  '/sessionEdit/' +  session.id, '_blank',
          'resizeable=yes, width=' + (500) + ', left=' + (500) + ', top=' + (22) + ', height=' + (700));
    },

    formatLocalDate(dt) { // Converts UTC ISO string to local formatted string
      if (!dt) return '';
      const m = moment.utc(dt);
      return m.isValid() ? m.local().format('DD MMM YYYY hh:mm A') : '';
    },

    getSearchableTime(dt) {// 17 Nov 2025 JCH Return time variations for flexible search (9:14, 09:14, 21:14)
      if (!dt) return '';
      const m = moment.utc(dt).local();
      return this.formatLocalDate(dt) + ' ' + m.format('h:mm') + ' ' + m.format('HH:mm');
    },

    formatTime(interval) {// need this because backend only formats time for Period data, not Log or Daily
      let days = interval.days ? interval.days.toString() : '';
      let hours = interval.hours ? interval.hours.toString().padStart(2, '0') : '00';
      let minutes = interval.minutes ? interval.minutes.toString().padStart(2, '0') : '00';
      let seconds = interval.seconds ? interval.seconds.toString().padStart(2, '0') : '00';
      let time = `${days}${days.length ? 'd ' : ''}${hours}:${minutes}:${seconds}`;
      return time;
    },

    getHours(totType) {
      this.totType = totType;// 'Log' returns session id, so set this flag to show edit icon
      this.loading = true;
      let h = { ...this.hoursSearch };// separate copy of hoursSearch, so setting username = '' won't show
      if (!h.username || h.username === 'All') h.username = '';// send '' to get period hours for All users
      usersAPIService.getHours(totType, h.username, h.type, h.formatted_login, h.logout, this.$router).then(data => {
        if ((data.logs && !data.logs.length) || (totType !== 'Log' && !data.length)) {// if data is empty, update noData msg
          this.noDataText = 'Notice: no sessions found.'
        } else {
          // data is returned in the following formats:
          // 'Log' {"logs":[],"sum":null,"core":null} OR {"logs":[{},{},{}...],"sum":"3460:26","core":"1993:24"}
          // 'Daily' [] OR [{"username":"larry","_hours":{"hours":3460,"minutes":26,"seconds":33},"_formatted":"3460:26:33"},{similar},{},...]
          // 'Period' [] OR [{"username":"larry","_hours":{"hours":3460,"minutes":26,"seconds":33},"_formatted":"3460:26:33"}]
          this.list = data.logs ? data.logs : data;
          this.listLength = this.list.length;
          if (totType === 'Log') {
            this.list.core = data.core;
            this.list.sum = data.sum;
            for (let entry of data.logs) {
              entry.time = this.formatTime(entry.session_interval);
              // 17 Nov 2025 JCH Add formatted dates with time variations for flexible search (9:14, 09:14, 09:14 PM, 21:14)
              // Only process entries with valid dates; skip date replacement for invalid dates but keep entry in list
              if (entry.login && entry.logout) {
                const loginMoment = moment.utc(entry.login);
                const logoutMoment = moment.utc(entry.logout);
                if (loginMoment.isValid() && logoutMoment.isValid()) {
                  const formattedLogin = this.formatLocalDate(entry.login);
                  const formattedLogout = this.formatLocalDate(entry.logout);
                  // Include displayed format + time variations in formatted_login so v-data-table search works
                  entry.formatted_login = formattedLogin + ' ' + loginMoment.local().format('h:mm') + ' ' + loginMoment.local().format('HH:mm');
                  entry.formatted_logout = formattedLogout + ' ' + logoutMoment.local().format('h:mm') + ' ' + logoutMoment.local().format('HH:mm');
                  // Replace raw ISO date strings with formatted versions to prevent false matches in search
                  // Store original for display/processing, but use formatted string for search
                  entry.login_iso = entry.login; // Keep original for reference
                  entry.logout_iso = entry.logout; // Keep original for reference
                  entry.login = formattedLogin + ' ' + loginMoment.local().format('h:mm') + ' ' + loginMoment.local().format('HH:mm');
                  entry.logout = formattedLogout + ' ' + logoutMoment.local().format('h:mm') + ' ' + logoutMoment.local().format('HH:mm');
                }
              }
            }
          }
          if (totType === 'Daily' || totType === 'Period') {
            for (let entry of data) {
              entry.formatted_login = entry._login;
              entry.time = this.formatTime(entry._hours);
            }
          }
          this.refresh = !this.refresh;
        }
        this.loading = false;
        window.scrollTo(0, document.body.scrollHeight);
      }).catch((e) => {
        this.loading = false;
        this.$refs.errorDialog.showError({
          errorMessage: "Failed to get users hours.",
          forDevelopers: e
        })
      });
    },

    sortHours(a, b) {
      return parseInt(a.replace(/:/g,'')) > parseInt(b.replace(/:/g,''));
    },
  },

}
</script>

<style>
.box {/* keep box-shadow for disabled buttons so they line up evenly with non-disabled button */
  box-shadow: rgba(0, 0, 0, 0.2) 0px 3px 1px -2px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px;
}
.nopad * {
  padding: 0 !important;
}
.down .v-text-field .v-input__control {
  font-weight: lighter;
  margin-left: 7px;
  margin-bottom: -20px;
}
/* 20 Nov 2025 JCH Ensure v-date-picker menu overlays app header on mobile/desktop */
.down .v-menu__content.menuable__content__active {
  z-index: 2000 !important;
}
/* 20 Nov 2025 JCH In some cases the menu is detached to body; raise z-index globally to clear the header */
.v-menu__content.menuable__content__active {
  z-index: 2000 !important;
}
</style>
